import React, { ReactElement, useEffect, useId, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

import {
    ALLOWED_REMOTE_VIEWER_ROLES,
    QA_CHECK_ROUTES_ALLOWED_ROLES,
    SCAN_TYPES_ROUTES_ALLOWED_ROLES,
    SCANNING_ROUTES_ALLOWED_ROLES,
    TABLET_MAX_WIDTH
} from '../AppConstants';
import { userHasRoleAccess } from '../helpers/AccessManagementHelpers';
import { logout } from '../helpers/AuthHelpers';
import { useCanViewSettings } from '../hooks/useCanView';
import { User } from '../types/User';
import Loader from './Loader';

import '../styles/components/SideNav.less';

interface Props {
    sideNavToggled: (val: boolean) => void;
    currentUser?: User;
    loadingUser?: boolean;
}

function SideNav({ sideNavToggled, currentUser, loadingUser }: Readonly<Props>): ReactElement {
    const { canView: canViewSettings } = useCanViewSettings();
    const [showSideBar, setShowSideBar] = useState(window.innerWidth > TABLET_MAX_WIDTH);
    const { pathname } = useLocation();

    useEffect(() => {
        window.addEventListener('resize', () => {
            if (window && window.innerWidth < TABLET_MAX_WIDTH) {
                setShowSideBar(false);
            }
        });
    }, []);

    useEffect(() => {
        sideNavToggled(showSideBar);
    }, [showSideBar]);

    const sidebarId = useId();
    const navigationLabelId = useId();

    return (
        <div
            id={sidebarId}
            role="complementary"
            className={clsx('side-nav', {
                'show-sidebar': showSideBar,
                'hide-sidebar': !showSideBar
            })}
            data-testid="nav-side-bar"
            aria-labelledby={navigationLabelId}
        >
            <ReactTooltip id="sidenav-tooltip" type="dark" effect="solid" />

            <button
                className="collapse-container"
                data-for="sidenav-tooltip"
                data-testid="toggle-side-bar"
                data-tip={!showSideBar ? 'Expand sidebar' : ''}
                onClick={() => setShowSideBar(!showSideBar)}
                aria-expanded={showSideBar}
                aria-controls={sidebarId}
            >
                <span
                    id={navigationLabelId}
                    role="heading"
                    className={clsx({
                        'sr-only': !showSideBar
                    })}
                >
                    Navigation Panel
                </span>
                <FontAwesomeIcon
                    icon={`${showSideBar ? 'chevron-double-left' : 'chevron-double-right'}`}
                    fixedWidth={true}
                />
            </button>

            {loadingUser && (
                <div className="spinner-container">
                    <Loader size={10} loading={loadingUser} overComponent={true} skipOverlay={true} />
                </div>
            )}

            <nav>
                {userHasRoleAccess(currentUser, SCANNING_ROUTES_ALLOWED_ROLES) && (
                    <>
                        <ReactTooltip id="scanning-tooltip" type="dark" effect="solid" />

                        <div className="side-nav-elem" role="menuitem" data-testid="nav-scanning">
                            <NavLink
                                to={`/scanning${
                                    userHasRoleAccess(currentUser, ALLOWED_REMOTE_VIEWER_ROLES)
                                        ? `?userLocation=${currentUser?.locationName}`
                                        : ''
                                }`}
                                className={(navData) => (navData.isActive ? 'selected' : '')}
                                data-tip={!showSideBar ? 'Scanning' : ''}
                                data-for="scanning-tooltip"
                            >
                                <FontAwesomeIcon icon="barcode" className="margin-right" fixedWidth={true} />
                                {showSideBar && <span>Scanning</span>}
                            </NavLink>
                        </div>
                    </>
                )}

                {userHasRoleAccess(currentUser, SCANNING_ROUTES_ALLOWED_ROLES) && (
                    <>
                        <ReactTooltip id="arriving-tooltip" type="dark" effect="solid" />

                        <div className="side-nav-elem" role="menuitem" data-testid="nav-arriving-loggers">
                            <NavLink
                                to="/arriving-loggers"
                                className={(navData) => (navData.isActive ? 'selected' : '')}
                                data-tip={!showSideBar ? 'Arriving Loggers' : ''}
                                data-for="arriving-tooltip"
                            >
                                <FontAwesomeIcon icon="shipping-timed" className="margin-right" fixedWidth={true} />
                                {showSideBar && <span>Arriving Loggers</span>}
                            </NavLink>
                        </div>
                    </>
                )}

                {userHasRoleAccess(currentUser, QA_CHECK_ROUTES_ALLOWED_ROLES) && (
                    <>
                        <ReactTooltip id="qa-tooltip" type="dark" effect="solid" />

                        <div className="side-nav-elem" role="menuitem" data-testid="nav-qa-check">
                            <NavLink
                                to="/qa-check"
                                className={(navData) => (navData.isActive ? 'selected' : '')}
                                data-testid="nav-link-quality-ac"
                                data-tip={!showSideBar ? 'Quality Assurance Check' : ''}
                                data-for="qa-tooltip"
                            >
                                <FontAwesomeIcon icon="check-square" className="margin-right" />
                                {showSideBar && <span>Quality Assurance Check</span>}
                            </NavLink>
                        </div>
                    </>
                )}

                {userHasRoleAccess(currentUser, SCAN_TYPES_ROUTES_ALLOWED_ROLES) && (
                    <>
                        <ReactTooltip id="scan-types-tooltip" type="dark" effect="solid" />

                        <div className="side-nav-elem" role="menuitem" data-testid="scan-types-navlink">
                            <NavLink
                                to="/scans/types"
                                className={(navData) => (navData.isActive ? 'selected' : '')}
                                data-testid="scan-types-tab-nav"
                                data-tip={!showSideBar ? 'Scan Types' : ''}
                                data-for="scan-types-tooltip"
                            >
                                <FontAwesomeIcon icon="tag" className="margin-right" />
                                {showSideBar && <span>Scan Types</span>}
                            </NavLink>
                        </div>
                    </>
                )}

                {canViewSettings && (
                    <>
                        <ReactTooltip id="settings-tooltip" type="dark" effect="solid" />

                        <div className="side-nav-elem" role="menuitem" data-testid="nav-settings">
                            <NavLink
                                to="/settings/my"
                                className={pathname.startsWith('/settings') ? 'selected' : ''}
                                data-tip={showSideBar ? '' : 'Settings'}
                                data-for="settings-tooltip"
                            >
                                <FontAwesomeIcon icon="cog" className="margin-right" fixedWidth={true} />
                                {showSideBar && <span>Settings</span>}
                            </NavLink>
                        </div>
                    </>
                )}
            </nav>

            <div
                className="log-out-elem"
                data-testid="nav-log-out"
                title="Sign Out"
                role="menuitem"
                data-tip={!showSideBar ? 'Sign Out' : ''}
                onClick={() => logout()}
            >
                <FontAwesomeIcon icon="sign-out" className="margin-right" />
                {showSideBar && <span>Sign out</span>}
            </div>
        </div>
    );
}

export default SideNav;
